/*
  adg-card — the one artwork card.

  Phase B step 1. Before this there were two: adg-artgrid.js built
  `.adg-card` (design grids, homepage rows via the Liquid snippet) and
  byo-builder.js built its own `.byo-card`. Same thing on screen, two
  implementations, so every improvement had to be made twice and they
  drifted. This is the single source.

  It is a JS factory rather than a Liquid <template> because both callers
  render from client-side data (the artwork index) — Liquid never sees these
  rows. The Liquid snippet `adg-art-card.liquid` stays as-is for the
  server-rendered surfaces; it already emits the same class family.

  WHAT VARIES between the two callers, and why each is a real difference,
  not drift to be flattened:

    mode 'link'   — design grids: the card navigates to the product.
    mode 'button' — BYO: the card PICKS. A div with role=button, because it
                    must not navigate and must be draggable for calendar
                    reordering. Not a toggle (no aria-pressed, 2026-10-07):
                    a second tap adds another copy; byo-builder.js describes
                    where the design already sits instead.

    expand 'wrap'   — a link cannot contain a button (invalid, and the
                      anchor eats the click), so the ⤢ goes in a sibling
                      wrapper. BYO uses it too (2026-10-07): a role=button
                      card cannot contain a button either (WCAG 4.1.2, axe
                      nested-interactive).
    expand 'inside' — puts the ⤢ inside the media. Only for a card that is
                      NOT itself interactive; no caller uses it today.

    crossorigin     — REQUIRED for BYO. The live-preview dock draws these
                      images into a canvas; without it the canvas is
                      tainted and the preview silently dies.

  SKIN (`prefix`). The two surfaces are styled differently today:
  `.adg-card` frames the MEDIA (Heather's Figma — 1:1, 1px #D8D9DD, 10px
  radius) while `.byo-card` frames the CARD. Same structure, different skin.
  So the factory emits whichever class family it is asked for, and step 2 is
  split: 2a unifies the DOM and behaviour with zero visual change (BYO keeps
  its skin), 2b switches BYO to the adg skin so the two finally look alike.
  Splitting it keeps the visual change in its own revertible commit instead
  of riding along with a structural one. When 2b lands, `prefix`, `bodyWrap`
  and `badge` all become dead options and should be deleted.

  OVERLAY SLOT. Every card gets `.adg-card__overlay`, an empty positioned
  container. BYO's pick badge goes in it today; favorites goes in it later.
  That is the point: a new card-level affordance is added once, here, and
  every surface gets it — rather than being retrofitted per grid.
*/
(function (global) {
  'use strict';

  // Both callers hold different field names for the same artwork. Normalise
  // once here instead of teaching the factory two vocabularies.
  function normalise(d) {
    return {
      id:     d.id     || d.h    || '',
      title:  d.title  || d.t    || '',
      artist: d.artist || d.a    || '',
      img:    d.img    || d.image || '',
      // Where this design lives on the CDN, so the card can ask for the
      // right rung and format instead of the one baked into `img`.
      cdn:    d.cdn    || '',
      url:    d.url    || '',
      isNew:  !!d.isNew
    };
  }

  /* Shimmer until the thumbnail decodes; cleared on load OR error so a
     broken image never shimmers forever. Cached images fire no event, hence
     the complete/naturalWidth check — both callers had this bug-fix
     separately, which is exactly the duplication this file ends. */
  function settleOnLoad(media, img) {
    var settle = function () { media.classList.remove('is-loading'); };
    img.addEventListener('load', settle, { once: true });
    img.addEventListener('error', settle, { once: true });
    if (img.complete && img.naturalWidth > 0) settle();
  }

  function create(data, opts) {
    var a = normalise(data);
    var o = opts || {};
    var mode = o.mode || 'link';

    var P = o.prefix || 'adg-card';
    var card = document.createElement(mode === 'button' ? 'div' : 'a');
    card.className = P + (o.className ? ' ' + o.className : '');

    if (mode === 'button') {
      card.setAttribute('role', 'button');
      card.setAttribute('tabindex', '0');
      if (a.id) card.dataset.id = a.id;
    } else {
      card.href = a.url;
    }

    var media = document.createElement('div');
    media.className = P + '__media is-loading';

    var img = document.createElement('img');
    img.alt = '';   // the card's title span names the card; a repeated alt is read twice
    if (o.crossorigin) img.crossOrigin = 'anonymous';
    if (o.eager) {
      img.loading = 'eager';
      img.setAttribute('fetchpriority', 'high');
    } else {
      img.loading = 'lazy';
    }
    if (o.decoding) img.decoding = 'async';
    if (o.imgClass) img.className = o.imgClass;
    if (mode === 'button') img.draggable = false;
    img.src = a.img;

    // Responsive + WebP, when the row came from the artwork index and so
    // knows its place on the CDN. Measured on this catalogue: WebP is 39%
    // smaller at 512 and 46% at 1024, and a card that only ever paints
    // ~250px wide has been downloading the 512 all along.
    //
    // <picture> rather than a bare srcset because only <source type> lets
    // the browser decline WebP; the <img> keeps JPEG candidates, so a
    // client without WebP still gets a right-sized file rather than a
    // broken one.
    if (a.cdn && global.ADGIndex) {
      // Must track the real grid, or the browser over-fetches: the design
      // grid is 5/4/3/2 columns at 1400/1100/700 (adg-collection.css:38-78),
      // which measures 248px of a 1499px viewport = 16.5vw. Guessing 20vw
      // made a 248px card download the 512 instead of the 256 — nearly
      // triple the bytes. Callers with a different grid pass their own.
      var sizes = o.sizes ||
        '(min-width: 1401px) 17vw, (min-width: 1101px) 21vw, ' +
        '(min-width: 701px) 28vw, 45vw';
      img.srcset = global.ADGIndex.setFrom(a.cdn, 'jpg');
      img.sizes = sizes;

      var picture = document.createElement('picture');
      var webp = document.createElement('source');
      webp.type = 'image/webp';
      webp.srcset = global.ADGIndex.setFrom(a.cdn, 'webp');
      webp.sizes = sizes;
      picture.appendChild(webp);
      picture.appendChild(img);
      settleOnLoad(media, img);
      media.appendChild(picture);
    } else {
      settleOnLoad(media, img);
      media.appendChild(img);
    }

    // The slot every future card-level affordance lands in — opt-in, so a
    // surface that has nothing to put there does not carry an empty div on
    // every one of 3,000 cards. BYO turns it on in step 2; when favorites
    // arrives it turns on everywhere, from this one line.
    if (o.overlay) {
      var overlay = document.createElement('div');
      overlay.className = P + '__overlay';
      media.appendChild(overlay);
    }

    if (o.badge) {
      var badge = document.createElement('span');
      badge.className = P + '__badge';
      badge.setAttribute('aria-hidden', 'true');
      badge.textContent = o.badge;
      media.appendChild(badge);
    }

    // "New" — a design added to the store within the merchant's window
    // (global theme setting). Distinct from the pick badge so both can
    // show at once; the same rule marks Liquid-rendered product cards.
    if (a.isNew) {
      var flag = document.createElement('span');
      flag.className = P + '__flag';
      flag.textContent = 'New';
      media.appendChild(flag);
    }

    card.appendChild(media);

    var title = document.createElement('span');
    title.className = P + '__title';
    title.textContent = a.title;
    // BYO wraps its title in a __body element; the design card does not.
    // One of the options 2b deletes.
    if (o.bodyWrap) {
      var body = document.createElement('div');
      body.className = P + '__body';
      body.appendChild(title);
      card.appendChild(body);
    } else {
      card.appendChild(title);
    }

    // Joel, email 1 item 5: no artist under the thumbnail. It survives in the
    // accessible name, the artist filter and the enlarged view.
    if (a.artist && o.showArtist) {
      var ar = document.createElement('span');
      ar.className = P + '__artist';
      ar.textContent = a.artist;
      card.appendChild(ar);
    }
    // A visible "New" flag starts the name (WCAG 2.5.3 Label in Name): a
    // speech user who says "click New…" must hit the card that shows it.
    if (a.artist && !o.showArtist) {
      card.setAttribute('aria-label', (a.isNew ? 'New, ' : '') + a.title + ', by ' + a.artist);
    }

    if (!o.expand) return card;

    if (o.expand === 'inside') {
      media.appendChild(o.expandButton);
      return card;
    }

    var wrap = document.createElement('div');
    wrap.className = P + '__wrap';
    wrap.appendChild(card);
    wrap.appendChild(o.expandButton);
    return wrap;
  }

  global.ADGCard = { create: create, normalise: normalise };
})(window);
